// ============================================================
// TaskFlow OS - Project Store (Zustand)
// ============================================================

import { create } from 'zustand';
import type { Project, ProjectMember, Workflow } from '../lib/types';
import { supabase, logEvent } from '../lib/supabase';

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  workflows: Workflow[];
  currentWorkflow: Workflow | null;
  loading: boolean;

  fetchProjects: (orgId: string) => Promise<void>;
  fetchProject: (projectId: string) => Promise<void>;
  createProject: (project: Partial<Project>) => Promise<{ data: Project | null; error: string | null }>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<{ error: string | null }>;
  deleteProject: (id: string) => Promise<{ error: string | null }>;
  setCurrentProject: (project: Project | null) => void;

  fetchWorkflows: (projectId: string) => Promise<void>;
  fetchWorkflow: (workflowId: string) => Promise<void>;
  createWorkflow: (workflow: Partial<Workflow>) => Promise<{ data: Workflow | null; error: string | null }>;
  updateWorkflow: (id: string, updates: Partial<Workflow>) => Promise<{ error: string | null }>;
  deleteWorkflow: (id: string) => Promise<{ error: string | null }>;
  setCurrentWorkflow: (workflow: Workflow | null) => void;

  addProjectMember: (projectId: string, userId: string, role: string) => Promise<{ error: string | null }>;
  removeProjectMember: (projectId: string, userId: string) => Promise<{ error: string | null }>;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  workflows: [],
  currentWorkflow: null,
  loading: false,

  fetchProjects: async (orgId) => {
    set({ loading: true });
    const { data, error } = await supabase
      .from('projects')
      .select('*, owner:profiles!owner_id(id, display_name, avatar_url)')
      .eq('organization_id', orgId)
      .is('archived_at', null)
      .order('sort_order', { ascending: true });

    if (!error && data) {
      set({ projects: data as Project[] });
    }
    set({ loading: false });
  },

  fetchProject: async (projectId) => {
    const { data, error } = await supabase
      .from('projects')
      .select('*, owner:profiles!owner_id(id, display_name, avatar_url), members:project_members(*, profile:profiles(*))')
      .eq('id', projectId)
      .single();

    if (!error && data) {
      set({ currentProject: data as Project });
    }
  },

  createProject: async (project) => {
    const { data, error } = await supabase
      .from('projects')
      .insert(project)
      .select()
      .single();

    if (error) return { data: null, error: error.message };

    const proj = data as Project;
    set({ projects: [...get().projects, proj] });

    await logEvent('project_created', { name: proj.name }, { projectId: proj.id });
    return { data: proj, error: null };
  },

  updateProject: async (id, updates) => {
    const { error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', id);

    if (error) return { error: error.message };

    set({
      projects: get().projects.map((p) => (p.id === id ? { ...p, ...updates } : p)),
      currentProject: get().currentProject?.id === id ? { ...get().currentProject!, ...updates } : get().currentProject,
    });

    await logEvent('project_updated', { updates }, { projectId: id });
    return { error: null };
  },

  deleteProject: async (id) => {
    const { error } = await supabase
      .from('projects')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id);

    if (error) return { error: error.message };

    set({ projects: get().projects.filter((p) => p.id !== id) });
    await logEvent('project_archived', {}, { projectId: id });
    return { error: null };
  },

  setCurrentProject: (project) => set({ currentProject: project }),

  fetchWorkflows: async (projectId) => {
    set({ loading: true });
    const { data, error } = await supabase
      .from('workflows')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (!error && data) {
      set({ workflows: data as Workflow[] });
    }
    set({ loading: false });
  },

  fetchWorkflow: async (workflowId) => {
    const { data, error } = await supabase
      .from('workflows')
      .select('*')
      .eq('id', workflowId)
      .single();

    if (!error && data) {
      set({ currentWorkflow: data as Workflow });
    }
  },

  createWorkflow: async (workflow) => {
    const { data, error } = await supabase
      .from('workflows')
      .insert(workflow)
      .select()
      .single();

    if (error) return { data: null, error: error.message };

    const wf = data as Workflow;
    set({ workflows: [wf, ...get().workflows] });

    await logEvent('workflow_created', { name: wf.name, type: wf.type }, {
      projectId: wf.project_id,
      workflowId: wf.id,
    });
    return { data: wf, error: null };
  },

  updateWorkflow: async (id, updates) => {
    const { error } = await supabase
      .from('workflows')
      .update(updates)
      .eq('id', id);

    if (error) return { error: error.message };

    set({
      workflows: get().workflows.map((w) => (w.id === id ? { ...w, ...updates } : w)),
      currentWorkflow: get().currentWorkflow?.id === id ? { ...get().currentWorkflow!, ...updates } : get().currentWorkflow,
    });
    return { error: null };
  },

  deleteWorkflow: async (id) => {
    const wf = get().workflows.find((w) => w.id === id);
    const { error } = await supabase.from('workflows').delete().eq('id', id);

    if (error) return { error: error.message };

    set({ workflows: get().workflows.filter((w) => w.id !== id) });
    if (wf) {
      await logEvent('workflow_deleted', { name: wf.name }, {
        projectId: wf.project_id,
        workflowId: id,
      });
    }
    return { error: null };
  },

  setCurrentWorkflow: (workflow) => set({ currentWorkflow: workflow }),

  addProjectMember: async (projectId, userId, role) => {
    const { error } = await supabase
      .from('project_members')
      .insert({ project_id: projectId, user_id: userId, role });

    if (error) return { error: error.message };

    await logEvent('member_added', { user_id: userId, role }, { projectId });
    return { error: null };
  },

  removeProjectMember: async (projectId, userId) => {
    const { error } = await supabase
      .from('project_members')
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', userId);

    if (error) return { error: error.message };

    await logEvent('member_removed', { user_id: userId }, { projectId });
    return { error: null };
  },
}));
